/**
 * Advanced Cut List — World-class format with full columns and grouping.
 * Drag-to-reorder is persisted in-component by part `code` so the custom
 * order survives wardrobe re-computes and flows through to CNC, Cutting
 * Layout, Excel and the branded PDF export.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, Download, FileText, GripVertical, RotateCcw } from 'lucide-react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const EDGE_MAP = {
  'Left Side Panel': 'L1 L2', 'Right Side Panel': 'L1 L2', 'Top Panel': 'L1 F1',
  'Bottom Panel': 'L1 F1', 'Back Panel': 'None', 'Shutter Left': 'All 4', 'Shutter Right': 'All 4',
};
const GRAIN_MAP = { '18mm Plywood': 'Lengthwise', '12mm Plywood': 'Any', '8mm Plywood': 'N/A', '16mm HDHMR': 'Lengthwise' };

export default function AdvancedCutList({ cutList = [], estimateName = '', customOrder: externalOrder, onCustomOrderChange }) {
  const [groupBy, setGroupBy] = useState('none');
  // iter-290: drag-to-reorder. Persist custom order by part code so reorders
  // survive wardrobe re-computes and propagate to PDF / Excel / CNC exports.
  // When parent supplies `customOrder` + `onCustomOrderChange`, we lift the
  // state to the page so the same reorder flows to Cutting Layout & CNC tabs.
  const [internalOrder, setInternalOrder] = useState([]);
  const customOrder = externalOrder !== undefined ? externalOrder : internalOrder;
  const setCustomOrder = onCustomOrderChange || setInternalOrder;
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const enriched = useMemo(() => cutList.map((item, idx) => {
    const thickness = item.material?.includes('18') ? '18mm' : item.material?.includes('12') ? '12mm' : item.material?.includes('8') ? '8mm' : item.material?.includes('16') ? '16mm' : '?';
    return {
      ...item, idx: idx + 1, thickness,
      finish: item.laminate === 'External' ? 'Premium Laminate' : item.laminate === 'Internal' ? 'Internal Laminate' : 'Raw',
      edgeSides: EDGE_MAP[item.part] || (item.part.includes('Shelf') ? 'F1' : item.part.includes('Drawer') ? 'F1' : 'None'),
      grain: GRAIN_MAP[item.material] || 'Any',
      machineNotes: item.part.includes('Side') ? 'Shelf pin holes' : item.part.includes('Shutter') ? 'Hinge cup boring' : item.part.includes('Bottom') && item.code.includes('D') ? 'Groove 8mm' : '',
      room: 'Master Bedroom',
      module: 'Wardrobe',
    };
  }), [cutList]);

  // Apply custom order to enriched. Items already in customOrder come first
  // (in that order), new items (e.g. after wardrobe reconfig adds a drawer)
  // append in their natural position.
  const ordered = useMemo(() => {
    if (!customOrder.length) return enriched;
    const rank = new Map(customOrder.map((code, i) => [code, i]));
    return [...enriched].sort((a, b) => {
      const ra = rank.has(a.code) ? rank.get(a.code) : 1e9 + a.idx;
      const rb = rank.has(b.code) ? rank.get(b.code) : 1e9 + b.idx;
      return ra - rb;
    });
  }, [enriched, customOrder]);

  const moveItem = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const codes = ordered.map(i => i.code);
    const [moved] = codes.splice(from, 1);
    codes.splice(to, 0, moved);
    setCustomOrder(codes);
  };
  const handleRowDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };
  const handleRowDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleRowDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx !== null) moveItem(dragIdx, idx);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleRowDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const resetOrder = () => setCustomOrder([]);

  const grouped = useMemo(() => {
    if (groupBy === 'none') return { 'All Parts': ordered };
    const g = {};
    ordered.forEach(item => {
      const key = groupBy === 'material' ? item.material : groupBy === 'thickness' ? item.thickness : groupBy === 'room' ? item.room : item.module;
      if (!g[key]) g[key] = [];
      g[key].push(item);
    });
    return g;
  }, [ordered, groupBy]);

  const exportExcel = () => {
    const wb = XLSX.utils.book_new();
    const rows = ordered.map(c => [c.code, c.part, c.qty, c.w, c.h, c.thickness, c.material, c.finish, c.laminate, c.edgeSides, c.grain, c.machineNotes, c.room, c.module]);
    const data = [['Code', 'Part', 'Qty', 'W (mm)', 'H (mm)', 'Thickness', 'Material', 'Finish', 'Laminate', 'Edge Sides', 'Grain', 'Machine Notes', 'Room', 'Module'], ...rows];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(data), 'Cut List');
    XLSX.writeFile(wb, `${(estimateName || 'CutList').replace(/\s+/g, '_')}_CutList.xlsx`);
  };

  const exportPDF = async () => {
    try {
      toast.info('Generating branded PDF...');
      const token = localStorage.getItem('token');
      const payload = {
        name: `${estimateName || 'Estimate'} - Cut List`,
        material_summary: [],
        cut_list: ordered.map(c => ({ code: c.code, part: c.part, qty: c.qty, w: c.w, h: c.h, material: c.material, laminate: c.finish })),
        calculations: {},
        total_cost: 0,
      };
      const res = await axios.post(`${API}/estimates/export-pdf`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(estimateName || 'CutList').replace(/\s+/g, '_')}_CutList.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Cut List PDF downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="advanced-cut-list">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2"><ClipboardList size={18} className="text-orange-600" /><h3 className="font-bold text-stone-800">Advanced Cut List</h3>
          <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full text-stone-600">{enriched.length} parts</span>
          <span className="text-[11px] text-stone-400 hidden sm:inline-flex items-center gap-1" data-testid="cutlist-reorder-hint">
            <GripVertical size={11} /> Drag rows to reorder
          </span>
          {customOrder.length > 0 && (
            <button
              onClick={resetOrder}
              className="text-[11px] text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
              title="Restore default order"
              data-testid="cutlist-reset-order"
            >
              <RotateCcw size={11} /> Reset order
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select className="h-8 text-xs rounded-md border border-stone-300 px-2" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="none">No Grouping</option>
            <option value="material">By Material</option>
            <option value="thickness">By Thickness</option>
            <option value="room">By Room</option>
          </select>
          <Button size="sm" variant="outline" onClick={exportPDF} data-testid="export-cutlist-pdf"><FileText size={14} className="mr-1" />PDF</Button>
          <Button size="sm" variant="outline" onClick={exportExcel} data-testid="export-cutlist-excel"><Download size={14} className="mr-1" />Excel</Button>
        </div>
      </div>

      {Object.entries(grouped).map(([group, items]) => (
        <Card key={group}>
          <CardContent className="pt-3 pb-2 px-2">
            {groupBy !== 'none' && <h4 className="font-semibold text-xs text-stone-500 uppercase tracking-wide mb-2 px-2">{group} ({items.length})</h4>}
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-stone-50 border-b">
                  {groupBy === 'none' && <th className="px-1 py-1.5 w-6"></th>}
                  {['Code', 'Part', 'Qty', 'W', 'H', 'Thk', 'Material', 'Finish', 'Edge', 'Grain', 'Notes'].map(h => (
                    <th key={h} className="px-2 py-1.5 text-left font-semibold text-stone-600 whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {items.map((c, i) => {
                    // When grouping is on, drag is disabled (drag-by-code can't
                    // honour group boundaries cleanly). When 'none', i === global index in `ordered`.
                    const dragEnabled = groupBy === 'none';
                    const isDragging = dragEnabled && dragIdx === i;
                    const isDropTarget = dragEnabled && dragOverIdx === i && dragIdx !== null && dragIdx !== i;
                    return (
                      <tr
                        key={c.code}
                        className={`border-b hover:bg-stone-50 transition-colors ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
                        draggable={dragEnabled}
                        onDragStart={dragEnabled ? handleRowDragStart(i) : undefined}
                        onDragOver={dragEnabled ? handleRowDragOver(i) : undefined}
                        onDrop={dragEnabled ? handleRowDrop(i) : undefined}
                        onDragEnd={dragEnabled ? handleRowDragEnd : undefined}
                        data-testid={`cutlist-row-${c.code}`}
                      >
                        {groupBy === 'none' && (
                          <td className="px-1 py-1.5 text-stone-300 hover:text-stone-600 cursor-grab active:cursor-grabbing select-none w-6" title="Drag to reorder" data-testid={`cutlist-drag-${c.code}`}>
                            <GripVertical size={12} />
                          </td>
                        )}
                        <td className="px-2 py-1.5 font-mono font-bold text-blue-700">{c.code}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">{c.part}</td>
                        <td className="px-2 py-1.5 text-center">{c.qty}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{c.w}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{c.h}</td>
                        <td className="px-2 py-1.5">{c.thickness}</td>
                        <td className="px-2 py-1.5">{c.material}</td>
                        <td className="px-2 py-1.5">{c.finish}</td>
                        <td className="px-2 py-1.5 text-stone-500">{c.edgeSides}</td>
                        <td className="px-2 py-1.5 text-stone-500">{c.grain}</td>
                        <td className="px-2 py-1.5 text-stone-400 italic">{c.machineNotes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
